import type { ListKey } from 'types/listViews';

/** The cells an entry spans on Home's grid, written columns x rows. */
export type HomeWidgetSize = '1x1' | '1x2' | '2x2' | '1x4' | '2x4';

/** A section every Home has, placed and sized like a widget but built and deleted by nobody. */
export type HomeBuiltIn = 'MY_CASES' | 'CONTINUE' | 'MY_TIME' | 'MY_TIME_RECORDS' | 'TO_REVIEW' | 'SAVED_FOR_LATER';

/** How a widget draws its figure. */
export type HomeWidgetForm = 'NUMBER' | 'PROGRESS' | 'TREND' | 'BREAKDOWN' | 'RANKED';

/** The span of time a figure covers, relative to the day it is read. `AS_SAVED` is a saved-view widget's only. */
export type HomeWidgetPeriod = 'TODAY' | 'THIS_WEEK' | 'THIS_MONTH' | 'YEAR_TO_DATE' | 'LAST_12_MONTHS' | 'AS_SAVED';

/**
 * The sizes each built-in and each form can take, as the backend checks them, smallest first: the first is the size
 * an entry is added at.
 */
export const HOME_BUILT_IN_SIZES: Record<HomeBuiltIn, HomeWidgetSize[]> = {
    MY_CASES: ['2x2', '1x4', '2x4'],
    CONTINUE: ['1x2', '2x2', '1x4'],
    MY_TIME: ['1x2', '2x2', '1x4'],
    MY_TIME_RECORDS: ['1x2', '1x4'],
    TO_REVIEW: ['1x2', '1x4'],
    SAVED_FOR_LATER: ['1x2', '1x4'],
};

export const HOME_WIDGET_FORM_SIZES: Record<HomeWidgetForm, HomeWidgetSize[]> = {
    NUMBER: ['1x1'],
    PROGRESS: ['1x1', '1x2'],
    TREND: ['1x2', '2x2'],
    BREAKDOWN: ['1x2', '2x2'],
    RANKED: ['1x2', '1x4'],
};

type DefinitionBase = {
    v: 1;
    form: HomeWidgetForm;
    period: HomeWidgetPeriod;
    /** Required for `PROGRESS`, never on `RANKED`. */
    target?: number;
    /** The size its author chose. */
    size: HomeWidgetSize;
};

/** A measure of the catalogue, with a filter in the grammar of the family's list. */
export type CatalogueDefinition = DefinitionBase & {
    source: 'CATALOGUE';
    family: string;
    measure: string;
    filter?: string;
    groupBy?: string;
    /** `BREAKDOWN` only. */
    drawnAs?: 'BARS' | 'DONUT';
};

/** The row count or one total of a list view: a saved view's id, or the key of one of the list's built-in views. */
export type SavedViewDefinition = DefinitionBase & {
    source: 'SAVED_VIEW';
    listKey: ListKey;
    view: number | string;
    show: 'COUNT' | 'TOTAL';
    /** With `TOTAL` only. */
    total?: string;
};

/** What a widget measures and how it is drawn. A field that does not apply is absent, never `null`. */
export type HomeWidgetDefinition = CatalogueDefinition | SavedViewDefinition;

/** What creates a widget and what replaces one: its name, at most {@link HOME_WIDGET_NAME_MAX} characters, and what it shows. */
export type HomeWidgetSave = { name: string; definition: HomeWidgetDefinition };

export const HOME_WIDGET_NAME_MAX = 80;

export type HomeWidget = {
    id: number;
    name: string;
    definition: HomeWidgetDefinition;
    owner: { id: number; name: string };
    /** Whether the person asking may change or delete it: its owner, and whoever shares widgets with the firm. */
    canEdit: boolean;
    lastModifiedAt: string;
};

/** What an entry puts on Home, before it has a size. */
export type HomePlacement = { kind: 'BUILT_IN'; builtIn: HomeBuiltIn } | { kind: 'WIDGET'; widget: HomeWidget };

export type HomeEntry = HomePlacement & { size: HomeWidgetSize };

export type Home = {
    /** What the firm placed: the person cannot move, resize or remove these. */
    locked: HomeEntry[];
    /** The person's own entries, in the order the grid places them. */
    own: HomeEntry[];
    maxOwnEntries: number;
};

export type HomeEntrySave =
    | { kind: 'BUILT_IN'; builtIn: HomeBuiltIn; size: HomeWidgetSize }
    | { kind: 'WIDGET'; widgetId: number; size: HomeWidgetSize };

/** The person's own entries, replacing the ones they had. */
export type HomeLayoutSave = { entries: HomeEntrySave[] };
